import { path } from 'd3-path'
import Symbol from '../symbol.js'
import * as colors from 'scoreboard-colors'

export default class Cube extends Symbol {

  handles = [{
    defaults: [0, .25],
    constraints: (dimensions, handles) => {
      const maxY = Math.min(dimensions.height  / 2, dimensions.width) / dimensions.height
      return [0, [0, maxY]]
    },
    cursor: 'ns-resize'
  }]

  getPaths (data, dimensions, handles) {
    const color = data.color
    const topColor = colors.adjustLightness(color, 35) // top is lighter
    const sideColor = colors.adjustLightness(color, -10) // side is darker
    const id0 = `${this.id}-0-component`
    const id1 = `${this.id}-1-component`
    const id2 = `${this.id}-2-component`

    const offset = dimensions.height * handles[0][1]

    const outline = this.outline(dimensions, data.strokeWidth, offset)

    // All paths share this clip
    let clip = {
      id: `${this.id}-clip`,
      path: outline,
    }

    // The first component is the main sheet
    return [{
      path: this.main(dimensions, offset),
      id: id0,
      fill: this.getFillValue(id0, data.fill, color),
      clip,
      gradient: {
        type: 'linearGradient',
        id: `shape-gradient-0-${this.id}`,
        x1: '30%',
        y1: '0%',
        x2: '100%',
        y2: '100%',
        stops: [
          { offset: '0%', color: colors.adjustLightness(color, 30) },
          { offset: '50%', color: color },
          { offset: '100%', color: colors.adjustLightness(color, -10) },
        ]
      }
    }, {
      // The second component is the top parallellogram
      path: this.top(dimensions, offset),
      id: id1,
      fill: this.getFillValue(id1, data.fill, topColor),
      clip,
      gradient: {
        type: 'linearGradient',
        id: `shape-gradient-1-${this.id}`,
        x1: '80%',
        y1: '0%',
        x2: '0%',
        y2: '100%',
        stops: [
          { offset: '0%', color: colors.adjustLightness(color, 10) },
          { offset: '50%', color: colors.adjustLightness(color, 35) },
          { offset: '100%', color: colors.adjustLightness(color, 50) },
        ]
      }
    }, {
      // The third component is the side parallellogram
      path: this.side(dimensions, offset),
      id: id2,
      fill: this.getFillValue(id2, data.fill, sideColor),
      clip,
      gradient: {
        type: 'linearGradient',
        id: `shape-gradient-2-${this.id}`,
        x1: '0%',
        y1: '0%',
        x2: '30%',
        y2: '100%',
        stops: [
          { offset: '0%', color: colors.adjustLightness(color, 15) },
          { offset: '50%', color: colors.adjustLightness(color, -10) },
          { offset: '100%', color: colors.adjustLightness(color, -20) },
        ]
      }
    }, {
      path: outline,
      fill: 'none',
      styles: {
        'stroke': 'none', // overwrite it
      }
    }]
  }

  getFlattenedPaths (data, dimensions) {
    // When handles are in the top left corner, the cube is flat
    const paths = this.getPaths(data, dimensions, [[0, 0]])
    paths.at(-1).fill = paths.at(0).fill
    return paths
  }

  outline (dimensions, stroke, offset) {
    const _path = path()
    const strokeOffset = stroke / 2
    _path.moveTo(offset - strokeOffset, -strokeOffset)
    _path.lineTo(dimensions.width + strokeOffset, -strokeOffset)
    _path.lineTo(dimensions.width + strokeOffset, dimensions.height - offset + strokeOffset)
    _path.lineTo(dimensions.width - offset, dimensions.height + strokeOffset)
    _path.lineTo(-strokeOffset, dimensions.height + strokeOffset)
    _path.lineTo(-strokeOffset, offset)
    _path.lineTo(offset - strokeOffset, -strokeOffset)
    _path.closePath()
    return _path.toString()
  }

  top (dimensions, offset) {
    const _path = path()

    _path.moveTo(offset, 0)
    _path.lineTo(dimensions.width, 0)
    _path.lineTo(dimensions.width - offset, offset)
    _path.lineTo(0, offset)
    _path.closePath()

    return _path.toString()
  }

  side (dimensions, offset) {
    const _path = path()

    _path.moveTo(dimensions.width, 0)
    _path.lineTo(dimensions.width, dimensions.height - offset)
    _path.lineTo(dimensions.width - offset, dimensions.height)
    _path.lineTo(dimensions.width - offset, offset)
    _path.closePath()

    return _path.toString()
  }

  main (dimensions, offset) {
    const _path = path()

    _path.moveTo(0, offset)
    _path.lineTo(dimensions.width - offset, offset)
    _path.lineTo(dimensions.width - offset, dimensions.height)
    _path.lineTo(0, dimensions.height)
    _path.lineTo(0, offset)
    _path.closePath()

    return _path.toString()
  }

  textContainer (dimensions, handles) {
    const offset = dimensions.height * handles[0][1]

    const width = dimensions.width - offset
    const height = dimensions.height - offset

    return {
      center: [width / 2, height / 2 + offset],
      width,
      height,
    }
  }

}
